/* ============================================================
   PARCU — header behaviour
   ------------------------------------------------------------
   1. Homepage only: the header + announcement bar float over the
      hero image, transparent, white text, with a soft scrim.
      Once you scroll past the threshold they fade to solid.
   2. A subcategory bar (built from the existing Shopify nav, so
      there is nothing extra to maintain) sits under the header.
      Hidden while the header is transparent over the hero.
   3. The wordmark gets a slow sheen while it's in white mode.

   All styling lives in assets/parcu-custom.css — this file only
   tags elements and toggles state classes on <html>:
      .parcu-hero      homepage with a hero image behind the header
      .parcu-scrolled  scrolled past the transparent threshold
   ============================================================ */

(function () {
    'use strict';

    /* Which nav entries feed the subcategory bar.
       - 'children'  -> use that menu item's dropdown links
       - 'self'      -> use the top level link itself
       Labels are matched case-insensitively against the nav text. */
    var SUBBAR_SOURCES = [
        { label: 'Heavyweight tees', take: 'children' },
        { label: 'Heavyweight Hoodies', take: 'self' }
    ];

    var SCROLL_THRESHOLD = 40;

    var root = document.documentElement;
    var headerSection, announceSection, subbar, ticking = false;
    var heroEls = [];
    /* Full-bleed dark sections the header should go clear over. Wider than
       heroEls: the category scroller belongs here but has no pills. */
    var darkEls = [];
    var menuOpen = false;

    function template() {
        try {
            if (window.__st && window.__st.p) return window.__st.p;
        } catch (e) {}
        return '';
    }

    function isHome() {
        var t = template();
        if (t) return t === 'home';
        var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return location.pathname === rootPath || location.pathname === rootPath.replace(/\/$/, '');
    }

    function isListing() {
        var t = template();
        if (t) return t === 'collection';
        return /^\/collections\//.test(location.pathname);
    }

    function isProduct() {
        var t = template();
        if (t) return t === 'product';
        return /^\/products\//.test(location.pathname);
    }

    function findSections() {
        var header = document.getElementById('mainHeader');
        headerSection = header ? header.closest('.shopify-section') || header.parentElement : null;
        announceSection = document.querySelector('[id*="freeship_banner"], [id*="header-marquee"]');

        if (headerSection) headerSection.setAttribute('data-parcu-header', '');
        if (announceSection) announceSection.setAttribute('data-parcu-announce', '');
    }

    /* ---------- subcategory bar ---------- */

    function collectLinks() {
        var nav = document.querySelector('#headerGrid .menu nav > ul');
        if (!nav) return [];

        var links = [];
        var seen = {};

        SUBBAR_SOURCES.forEach(function (source) {
            var wanted = source.label.toLowerCase();

            Array.prototype.forEach.call(nav.children, function (li) {
                var labelEl = li.querySelector(':scope > a, :scope > details > summary');
                if (!labelEl) return;
                if (labelEl.textContent.trim().toLowerCase() !== wanted) return;

                var picked = source.take === 'children'
                    ? Array.prototype.slice.call(li.querySelectorAll('ul a'))
                    : Array.prototype.slice.call(li.querySelectorAll(':scope > a')).slice(0, 1);

                picked.forEach(function (a) {
                    var href = a.getAttribute('href');
                    var text = a.textContent.trim();
                    if (!href || !text || seen[href + text]) return;
                    seen[href + text] = true;
                    links.push({ href: href, text: text });
                });
            });
        });

        return links;
    }

    function buildSubbar() {
        if (!headerSection) return;

        var links = collectLinks();
        if (links.length < 2) return;

        subbar = document.createElement('nav');
        subbar.className = 'parcu-subbar';
        subbar.setAttribute('data-parcu-subbar', '');
        subbar.setAttribute('aria-label', 'Shop categories');

        var ul = document.createElement('ul');
        var here = location.pathname;

        links.forEach(function (link) {
            var li = document.createElement('li');
            var a = document.createElement('a');
            a.href = link.href;
            a.textContent = link.text;
            if (link.href === here) a.setAttribute('aria-current', 'page');
            li.appendChild(a);
            ul.appendChild(li);
        });

        subbar.appendChild(ul);
        headerSection.insertAdjacentElement('afterend', subbar);
    }

    /* ---------- hero pills ----------
       The first homepage hero ships with a label on the left and a
       "Shop now" button on the right. Same two positions, restyled as
       translucent pills and pointed at the two collections. Reusing the
       theme's own elements keeps their layout and responsive rules. */

    var HERO_PILLS = [
        { label: 'Tees', href: '/collections/heavyweight' },
        { label: 'Hoodies', href: '/collections/heavyweight-hoodie' }
    ];

    /* The theme paints these from the merchant's hero colour settings with
       a rule that outranks anything a stylesheet can say, so the pill look
       goes on inline with !important. Ugly, but it is the only thing that
       actually wins.

       Consequence: an inline !important declaration also outranks a
       stylesheet :hover rule, so the hover state cannot live in CSS —
       it has to be swapped here too. */
    /* A white tint is invisible on a white banner, which is what the
       hoodie and new-arrivals photographs now are: the pill read as an
       empty outline there while the tees one, sitting over a model,
       looked frosted. A neutral tint darkens instead of lightening, so
       it shows on white and still reads as glass over a photograph. */
    var PILL_STYLE = {
        'background-color': 'rgba(0, 0, 0, 0.05)',
        'border': '1px solid rgba(0, 0, 0, 0.16)',
        'border-radius': '999px',
        'color': '#000'
    };

    var PILL_HOVER = {
        'background-color': 'rgba(0, 0, 0, 0.13)',
        'border': '1px solid rgba(0, 0, 0, 0.28)',
        'border-radius': '999px',
        'color': '#000'
    };

    function paintPill(el, style) {
        Object.keys(style).forEach(function (prop) {
            el.style.setProperty(prop, style[prop], 'important');
        });
    }

    function applyPillStyle(el) {
        el.classList.add('parcu-heropill');
        paintPill(el, PILL_STYLE);

        ['mouseenter', 'focus'].forEach(function (evt) {
            el.addEventListener(evt, function () { paintPill(el, PILL_HOVER); });
        });

        ['mouseleave', 'blur'].forEach(function (evt) {
            el.addEventListener(evt, function () { paintPill(el, PILL_STYLE); });
        });
    }

    /* Lift each pill out to be a direct child of its hero so it can be
       anchored to the banner itself rather than wherever the theme's own
       layout drops it. Placement is then purely CSS: bottom-right on
       desktop, centred at the bottom on phones. */

    function placeHeroPill() {
        if (!root.classList.contains('parcu-hero')) return;

        heroEls.forEach(function (hero) {
            var pill = hero.querySelector('.parcu-heropill');
            if (!pill || pill.parentNode === hero) return;

            hero.appendChild(pill);
            pill.classList.add('parcu-heropill--floating');
        });
    }

    function buildHeroPills() {
        if (!root.classList.contains('parcu-hero')) return;

        heroEls.forEach(function (hero, index) {
            if (hero.classList.contains('parcu-has-pills')) return;

            var label = hero.querySelector('.hasBg');
            var button = hero.querySelector('a.button.is-customized');
            if (!label && !button) return;

            /* One pill per banner. Where the theme gives us both a heading
               and a button, the button wins and the heading is retired —
               two pills side by side pointing at the same place is noise. */
            var pill = button || label;
            if (label && button) label.classList.add('parcu-heropill-off');

            var conf = HERO_PILLS[index];

            /* Where the theme only gave us a heading, swapping it for a real
               anchor is what makes the pill clickable — and keeps middle-click,
               "open in new tab" and crawlers working, which a click handler on
               an <h2> would not. Classes carry over so it still looks the same. */
            if (conf && conf.href && pill.tagName !== 'A') {
                var link = document.createElement('a');
                link.className = pill.className;
                link.setAttribute('href', conf.href);
                pill.parentNode.replaceChild(link, pill);
                pill = link;
            }

            applyPillStyle(pill);

            if (conf) {
                pill.textContent = conf.label;
                if (pill.tagName === 'A') pill.setAttribute('href', conf.href);
            }

            hero.classList.add('parcu-has-pills');
        });
    }

    /* ---------- dropdown panels ----------
       The nav panels are absolutely positioned, so over the hero they
       hang across the shipping banner. Measuring an open panel and
       padding the header by that much makes the header itself grow,
       which moves the banner down instead of being covered. */

    function measureOpenMenu() {
        var header = document.getElementById('mainHeader');
        if (!header) return;

        var open = header.querySelector('details[open] > details-menu');
        var height = open ? Math.round(open.getBoundingClientRect().height) : 0;

        root.style.setProperty('--parcuMenuH', height + 'px');
    }

    function watchDropdowns() {
        var header = document.getElementById('mainHeader');
        if (!header) return;

        header.addEventListener('toggle', function () {
            /* let the panel finish laying out before measuring */
            requestAnimationFrame(function () {
                menuOpen = !!header.querySelector('details[open]');
                root.classList.toggle('parcu-menuopen', menuOpen);
                measureOpenMenu();
                updateState();
            });
        }, true);
    }

    /* ---------- logo ---------- */

    function prepLogo() {
        var logo = document.querySelector('#headerGrid img.logo');
        if (!logo) return;

        var link = logo.closest('a');
        if (link) link.classList.add('parcu-logo-link');

        var apply = function () {
            var src = logo.currentSrc || logo.src;
            if (src) root.style.setProperty('--parcuLogoUrl', 'url("' + src + '")');
        };

        if (logo.complete) apply();
        logo.addEventListener('load', apply);
    }

    /* ---------- product description: pull out the model notes ----------
       The description is written as a run of <p>s where the last one or
       two are fit references ("Luka is 183cm wearing size medium"). They
       read as a different kind of information to the copy above, so they
       get grouped into their own panel instead of extending the wall of
       text. Styling lives in parcu-custom.css. */

    var MODEL_LINE = /\b\d{2,3}\s?cm\b|wearing (a )?size|\bwears\b/i;

    function groupModelNotes() {
        var desc = document.querySelector('product-content .description product-description, product-content .description .rte');
        if (!desc || desc.querySelector('.parcu-modelnote')) return;

        var paras = Array.prototype.filter.call(desc.children, function (node) {
            return node.tagName === 'P';
        });

        var groups = [];
        var run = [];

        paras.forEach(function (p) {
            if (MODEL_LINE.test(p.textContent)) {
                run.push(p);
            } else if (run.length) {
                groups.push(run);
                run = [];
            }
        });
        if (run.length) groups.push(run);

        groups.forEach(function (group) {
            var box = document.createElement('div');
            box.className = 'parcu-modelnote';
            group[0].parentNode.insertBefore(box, group[0]);
            group.forEach(function (p) { box.appendChild(p); });
        });
    }

    /* ---------- measurements + scroll state ---------- */

    function measure() {
        var headerH = headerSection ? headerSection.offsetHeight : 0;
        var announceH = announceSection ? announceSection.offsetHeight : 0;

        root.style.setProperty('--parcuHeaderH', headerH + 'px');
        /* what the hero has to slide up under: header + announcement,
           measured with the subbar collapsed */
        if (!root.classList.contains('parcu-scrolled')) {
            root.style.setProperty('--parcuHeaderStack', (headerH + announceH) + 'px');
        }
    }

    /* The header is transparent for exactly as long as it is physically
       sitting on top of a hero image — not for a fixed number of pixels,
       and not only the first one. Scrolling onto the second banner
       further down the homepage puts it back into white mode. */
    function isOverHero() {
        if (!darkEls.length || !headerSection) return false;

        var bar = headerSection.getBoundingClientRect();

        return darkEls.some(function (el) {
            var box = el.getBoundingClientRect();
            return box.top < bar.bottom - 2 && box.bottom > bar.top + 2;
        });
    }

    /* An open dropdown forces the solid header too — a menu panel
       hanging half-transparent over a photo is unreadable. */
    function updateState() {
        var solid = root.classList.contains('parcu-hero')
            ? !isOverHero()
            : window.scrollY > SCROLL_THRESHOLD;

        root.classList.toggle('parcu-scrolled', solid || menuOpen);
    }

    function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
            updateState();
            ticking = false;
        });
    }

    /* ---------- boot ---------- */

    function init() {
        findSections();
        buildSubbar();
        prepLogo();
        groupModelNotes();

        heroEls = Array.prototype.slice.call(
            document.querySelectorAll('#mainContent > * .hero, #mainContent > .hero')
        );

        /* Heroes plus anything else full-bleed and dark enough to carry a
           white nav — currently the category scroller, which supplies its
           own scrim. */
        darkEls = heroEls.concat(Array.prototype.slice.call(
            document.querySelectorAll('#mainContent > * .parcu-scroller, #mainContent > .parcu-scroller')
        ));

        if (isHome() && darkEls.length && headerSection) {
            root.classList.add('parcu-hero');
        }

        buildHeroPills();
        placeHeroPill();
        watchDropdowns();

        if (isListing()) {
            root.classList.add('parcu-listing');
        }

        if (isProduct()) {
            root.classList.add('parcu-product');
        }

        measure();
        onScroll();

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', measure);
        window.addEventListener('load', measure);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();


/* ============================================================
   PARCU — product card colourways
   ------------------------------------------------------------
   Cards already carry a row of colour dots (.pc-swatches), one per
   sibling colourway, each holding that colourway's photo, URL, title
   and price. That row is the only data source this needs, so the card
   markup itself is untouched.

   Touch: the media becomes a scroll-snap track — swipe moves through
   the colourways, one swipe per colour. The product's own second photo
   drops out of the track (CSS) so a swipe never means two things.
   Desktop: the stack is left alone and small arrows step through the
   same list by swapping the visible image.

   Either way the card's title, price and links follow the active
   colour, so tapping the card opens the colour you are looking at.
   ============================================================ */

(function () {
    'use strict';

    var TOUCH = !(window.matchMedia && window.matchMedia('(hover: hover)').matches);

    /* The hover-swap handler in the layout restores the card image from
       these attributes, so anything that changes the image has to keep
       them current or hovering a dot will snap back to the wrong colour. */
    function rememberOriginal(img) {
        if (img.dataset.pcOrigDone !== undefined) return;
        img.dataset.pcOrigSrc = img.getAttribute('src') || '';
        img.dataset.pcOrigSrcset = img.getAttribute('srcset') || '';
        img.dataset.pcOrigSizes = img.getAttribute('sizes') || '';
        img.dataset.pcOrigDone = '1';
    }

    function showImage(img, src) {
        rememberOriginal(img);
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.setAttribute('src', src);
        img.dataset.pcOrigSrc = src;
        img.dataset.pcOrigSrcset = '';
        img.dataset.pcOrigSizes = '';
    }

    function buildStates(card, row) {
        var dots = Array.prototype.slice.call(row.querySelectorAll('.pc-swatch'));
        if (dots.length < 2) return null;

        var current = row.querySelector('.pc-swatch.is-current');
        if (!current) return null;

        var titleLink = card.querySelector('.title a');
        var mediaLink = card.querySelector('.media > a');
        var priceCell = card.querySelector('[ref="price"]');

        var here = {
            dot: current,
            img: '',
            url: (mediaLink && mediaLink.getAttribute('href')) ||
                 (titleLink && titleLink.getAttribute('href')) || '',
            title: titleLink ? titleLink.textContent.trim() : '',
            price: ''
        };

        var states = [here];

        dots.forEach(function (dot) {
            if (dot === current) return;
            var src = dot.getAttribute('data-img');
            if (!src) return;
            states.push({
                dot: dot,
                img: src,
                url: dot.getAttribute('href') || '',
                title: dot.getAttribute('data-title') || dot.getAttribute('title') || '',
                price: dot.getAttribute('data-price') || ''
            });
        });

        return states.length > 1 ? states : null;
    }

    function setup(card) {
        if (card.dataset.pcw) return;

        var row = card.querySelector('.pc-swatches');
        if (!row) return;

        var track = card.querySelector('.media x-grid[columns="stack"]');
        var img = card.querySelector('.media img');
        if (!track || !img) return;

        var states = buildStates(card, row);
        if (!states) return;

        card.dataset.pcw = '1';
        card.setAttribute('has-colourways', '');

        var titleLink = card.querySelector('.title a');
        var mediaLink = card.querySelector('.media > a');
        var priceCell = card.querySelector('[ref="price"]');
        var footer = card.querySelector('.footer');
        var media = card.querySelector('.media');

        /* The price cell can hold a compare-at pair or a range, so the
           original markup is kept whole and put back rather than rebuilt. */
        if (priceCell) states[0].price = priceCell.innerHTML;

        var index = 0;

        function applyMeta(i) {
            var st = states[i];

            if (titleLink) {
                titleLink.textContent = st.title;
                if (st.url) {
                    titleLink.setAttribute('href', st.url);
                    titleLink.setAttribute('data-href', st.url);
                }
            }

            if (mediaLink && st.url) {
                mediaLink.setAttribute('href', st.url);
                mediaLink.setAttribute('data-href', st.url);
            }

            if (priceCell) {
                if (i === 0) {
                    priceCell.innerHTML = states[0].price;
                } else if (st.price) {
                    priceCell.textContent = st.price;
                }
            }

            /* Quick buy posts the card's own variant, which is the wrong
               product once you have moved off the first colour. */
            if (footer) footer.classList.toggle('pc-hide', i !== 0);

            states.forEach(function (s, n) {
                if (s.dot) s.dot.classList.toggle('is-active', n === i);
            });

            index = i;
        }

        if (TOUCH) {
            /* One slide per colourway, appended after the card's own photo.
               Cloning the real <img> keeps the theme's sizing rules — they
               are written against product-card .media img. */
            states.slice(1).forEach(function (st) {
                var slide = document.createElement('picture');
                slide.className = 'pc-sib';
                var clone = document.createElement('img');
                clone.setAttribute('src', st.img);
                clone.setAttribute('alt', st.title);
                clone.setAttribute('loading', 'lazy');
                clone.setAttribute('decoding', 'async');
                slide.appendChild(clone);
                track.appendChild(slide);
            });

            /* Claim the track before the layout's own carousel script sees
               it, so the dot count matches the slides we actually show. */
            track.dataset.pcDots = '1';

            var indicator = document.createElement('div');
            indicator.className = 'pc-dots';
            states.forEach(function (_, i) {
                var d = document.createElement('i');
                if (i === 0) d.classList.add('on');
                indicator.appendChild(d);
            });
            if (media) media.appendChild(indicator);

            track.addEventListener('scroll', function () {
                var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
                if (i < 0) i = 0;
                if (i > states.length - 1) i = states.length - 1;
                if (i === index) return;

                var pips = indicator.querySelectorAll('i');
                for (var j = 0; j < pips.length; j++) {
                    pips[j].classList.toggle('on', j === i);
                }
                applyMeta(i);
            }, { passive: true });

            return;
        }

        /* Desktop: step through the same list by swapping the image. */
        function step(delta) {
            var i = (index + delta + states.length) % states.length;

            if (i === 0) {
                if (media) media.classList.remove('pc-lock');
                if (states[0].img) {
                    showImage(img, states[0].img);
                } else if (img.dataset.pcBaseSrc !== undefined) {
                    img.setAttribute('src', img.dataset.pcBaseSrc);
                    if (img.dataset.pcBaseSrcset) img.setAttribute('srcset', img.dataset.pcBaseSrcset);
                    if (img.dataset.pcBaseSizes) img.setAttribute('sizes', img.dataset.pcBaseSizes);
                    img.dataset.pcOrigSrc = img.dataset.pcBaseSrc;
                    img.dataset.pcOrigSrcset = img.dataset.pcBaseSrcset;
                    img.dataset.pcOrigSizes = img.dataset.pcBaseSizes;
                }
            } else {
                if (img.dataset.pcBaseSrc === undefined) {
                    img.dataset.pcBaseSrc = img.getAttribute('src') || '';
                    img.dataset.pcBaseSrcset = img.getAttribute('srcset') || '';
                    img.dataset.pcBaseSizes = img.getAttribute('sizes') || '';
                }
                /* Pinning the pair stops the theme's hover cross-fade from
                   revealing the first colour's second photo underneath. */
                if (media) media.classList.add('pc-lock');
                showImage(img, states[i].img);
            }

            applyMeta(i);
        }

        var arrows = document.createElement('div');
        arrows.className = 'pc-arrows';

        /* A bare chevron, no button chrome — same geometry as the one on
           the reference site: 2px round-capped stroke, drawn in currentColor
           so the CSS decides the colour. The viewBox is cropped tight to the
           stroke (the reference's 24-unit box is mostly empty space, which
           would hold the glyph away from the card edge). */
        var CHEVRON = '<svg viewBox="9 5.9 7.5 13.1" aria-hidden="true" focusable="false">' +
            '<polyline points="10 17.83 15.4 12.43 10 7.03" ' +
            'fill="none" stroke="currentColor" stroke-width="2" ' +
            'stroke-linecap="round" stroke-miterlimit="8"></polyline></svg>';

        [['prev', -1], ['next', 1]].forEach(function (conf) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'pc-arrow pc-arrow--' + conf[0];
            b.setAttribute('aria-label', conf[0] === 'prev' ? 'Previous colour' : 'Next colour');
            b.innerHTML = CHEVRON;
            b.addEventListener('click', function (e) {
                /* The arrows sit over a card that is one big link. */
                e.preventDefault();
                e.stopPropagation();
                step(conf[1]);
            });
            arrows.appendChild(b);
        });

        if (media) media.appendChild(arrows);
    }

    function scan() {
        document.querySelectorAll('product-card').forEach(setup);
    }

    function start() {
        scan();

        /* Filters and infinite scroll swap whole grids in. */
        var pending = null;
        var mo = new MutationObserver(function () {
            if (pending) return;
            pending = window.setTimeout(function () {
                pending = null;
                scan();
            }, 120);
        });
        mo.observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
